import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

const ALL_WORKBOOKS = [
  { day: 1, title: 'Foundation and Prompting Fundamentals', desc: 'RICE prompting, your first AI task, and building the habit that carries through all 10 days.' },
  { day: 2, title: 'Advanced Prompting', desc: 'Chain it. Iterate it. Stop starting over.' },
  { day: 3, title: 'Use Case Identification', desc: 'Map your workflow. Find where AI actually belongs.' },
  { day: 4, title: 'Role-Specific Application', desc: 'Stop writing prompts for everyone. Start writing prompts for you.' },
  { day: 5, title: 'Workflow Redesign', desc: 'Don\'t improve the old workflow. Build a better one.' },
  { day: 6, title: 'Agent Basics', desc: 'Prompts are things you run. Agents are things that run for you.' },
  { day: 7, title: 'Agent Building', desc: 'Spec it before you build it. Test it before you trust it.' },
  { day: 8, title: 'Deploy and Iterate', desc: 'One fix at a time. One test at a time. That\'s how agents compound.' },
  { day: 9, title: 'Vibe Coding Part 1', desc: 'Describe it. Build it. Test it. One change at a time.' },
  { day: 10, title: 'Vibe Coding Part 2 + Program Close', desc: "You didn't just learn about AI. You built with it." },
];

export default function Workbooks({ user, onNav, onSelect }) {
  const [assessmentData, setAssessmentData] = useState(null);
  const [workbookProgress, setWorkbookProgress] = useState({});
  const [engagementData, setEngagementData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [assessSnap, wbSnap, profileSnap] = await Promise.all([
        getDoc(doc(db, 'assessments', user.uid)),
        getDoc(doc(db, 'workbooks', user.uid)),
        getDoc(doc(db, 'users', user.uid)),
      ]);

      if (assessSnap.exists()) setAssessmentData(assessSnap.data());
      if (wbSnap.exists()) setWorkbookProgress(wbSnap.data().progress || {});

      if (profileSnap.exists()) {
        const profile = profileSnap.data();
        // Read day range from user profile — set per participant by admin
        // Fall back to 1-10 if no range assigned
        setEngagementData({
          dayStart: profile.dayRangeStart || 1,
          dayEnd: profile.dayRangeEnd || 10,
        });
      }

      setLoading(false);
    };
    load();
  }, [user.uid]);

  if (loading) return <div style={{ color: '#8A8A8A', fontSize: '14px' }}>Loading your workbooks...</div>;

  const hasBaseline = !!assessmentData?.pre;

  // Read day range from user profile (set per participant by admin)
  // Fall back to 1-10 if no range has been assigned
  const dayStart = engagementData?.dayStart || 1;
  const dayEnd = engagementData?.dayEnd || 10;

  // Filter to assigned days only
  const assignedWorkbooks = ALL_WORKBOOKS.filter(wb => wb.day >= dayStart && wb.day <= dayEnd);
  const totalDays = assignedWorkbooks.length;

  const getStatus = (day) => {
    const prog = workbookProgress[`day${day}`];
    if (prog?.status === 'complete') return 'complete';
    if (prog?.sections && Object.keys(prog.sections).length > 0) return 'inprog';
    return 'notstarted';
  };

  // A day is accessible if the participant has a baseline AND the day is in their assigned range.
  // No progression gating — all assigned days are open.
  const isAccessible = (day) => {
    return hasBaseline && day >= dayStart && day <= dayEnd;
  };

  const completedDays = assignedWorkbooks.filter(wb => getStatus(wb.day) === 'complete').length;

  return (
    <div>
      <div className="page-heading">Your Workbooks</div>
      <div className="page-sub">
        Your program covers Day{dayStart === dayEnd ? '' : 's'} {dayStart}{dayStart !== dayEnd ? ` through ${dayEnd}` : ''}.
        All workbooks are open. Complete them in order or jump to where you are in the program.
      </div>

      <div style={styles.accessBanner}>
        <div>
          <div style={styles.accessDays}>{completedDays}<span style={{ fontSize: '18px', color: 'rgba(27,42,74,0.4)', fontWeight: '400' }}>/{totalDays}</span></div>
          <div style={styles.accessLabel}>workbooks completed</div>
        </div>
        <div style={{ fontSize: '12px', color: '#8A8A8A', textAlign: 'right' }}>
          Days {dayStart}–{dayEnd} assigned<br />
          Your answers save automatically
        </div>
      </div>

      {!hasBaseline && (
        <div style={styles.noAssessment}>
          <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', marginBottom: '8px' }}>Complete your AI Readiness Snapshot first</div>
          <div style={{ fontSize: '13px', color: '#8A8A8A', marginBottom: '16px', fontWeight: '300' }}>Your workbooks unlock after you take the baseline assessment. It takes about 8 minutes.</div>
          <button className="btn-primary" onClick={() => onNav('assessment')}>Take the Snapshot →</button>
        </div>
      )}

      <div style={styles.wbGrid}>
        {assignedWorkbooks.map(wb => {
          const accessible = isAccessible(wb.day);
          const status = getStatus(wb.day);
          const pct = status === 'complete' ? 100 :
            status === 'inprog' ? Math.min(80, Math.round((Object.keys(workbookProgress[`day${wb.day}`]?.sections || {}).length / 3) * 100)) : 0;

          return (
            <div
              key={wb.day}
              style={{ ...styles.wbCard, ...(!accessible ? styles.wbLocked : {}) }}
              onClick={() => accessible && onSelect(wb)}
            >
              {!accessible && <div style={styles.lockIcon}>🔒</div>}
              <div style={styles.wbTag}>Day {wb.day}</div>
              <div style={styles.wbTitle}>{wb.title}</div>
              <div style={styles.wbDesc}>{wb.desc}</div>
              <div style={styles.wbProgressBar}>
                <div style={{ ...styles.wbProgressFill, width: `${pct}%` }} />
              </div>
              <div style={styles.wbFooter}>
                <div style={{
                  ...styles.wbStatus,
                  color: status === 'complete' ? '#1a8a4a' : status === 'inprog' ? '#D4A847' : accessible ? '#8A8A8A' : '#C0C0C0'
                }}>
                  {status === 'complete' ? 'Complete' : status === 'inprog' ? 'In Progress' : accessible ? 'Not Started' : 'Take your assessment first'}
                </div>
                {accessible && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <a
                      href={`#/workbook/${wb.day}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ fontSize: '11px', color: '#185fa5', textDecoration: 'none', whiteSpace: 'nowrap' }}
                      title="Open this workbook in its own tab"
                    >⤢ New tab</a>
                    <div style={styles.wbAction}>
                      {status === 'complete' ? 'Review →' : status === 'inprog' ? 'Continue →' : 'Start →'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  accessBanner: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' },
  accessDays: { fontFamily: 'Playfair Display, serif', fontSize: '28px', fontWeight: '900', color: '#1B2A4A' },
  accessLabel: { fontSize: '11px', color: '#8A8A8A', fontWeight: '300' },
  noAssessment: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '28px', marginBottom: '24px', maxWidth: '480px' },
  wbGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: '16px', marginBottom: '28px' },
  wbCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', position: 'relative', cursor: 'pointer', transition: 'border-color 0.15s' },
  wbLocked: { opacity: 0.5, cursor: 'not-allowed' },
  lockIcon: { position: 'absolute', top: '16px', right: '16px', fontSize: '14px' },
  wbTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#C0392B', marginBottom: '8px' },
  wbTitle: { fontFamily: 'Playfair Display, serif', fontSize: '15px', fontWeight: '700', color: '#1B2A4A', marginBottom: '8px', lineHeight: '1.3' },
  wbDesc: { fontSize: '12px', color: '#8A8A8A', fontWeight: '300', lineHeight: '1.6', marginBottom: '16px' },
  wbProgressBar: { height: '3px', background: '#E8E6E2', marginBottom: '12px' },
  wbProgressFill: { height: '100%', background: 'linear-gradient(90deg,#C0392B,#D4A847)' },
  wbFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  wbStatus: { fontSize: '11px', fontWeight: '600' },
  wbAction: { fontSize: '11px', fontWeight: '600', color: '#1B2A4A', textDecoration: 'underline' },
};
